// Asset optimization utilities for better performance
// This module provides optimized asset loading and caching strategies

// Lazy load images with intersection observer
export const lazyLoadImage = (src, placeholder = '/images/placeholder.svg') => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => resolve(src);
    img.onerror = () => {
      console.warn(`Failed to load image: ${src}, using placeholder`);
      resolve(placeholder);
    };
    
    // Use intersection observer for better performance
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            img.src = src;
            observer.unobserve(entry.target);
          }
        });
      });
      
      // Create a dummy element to observe
      const dummyElement = document.createElement('div');
      observer.observe(dummyElement);
    } else {
      // Fallback for older browsers
      img.src = src;
    }
  });
};

// Preload critical assets
export const preloadCriticalAssets = () => {
  const criticalAssets = [
    '/static/css/main.css',
    '/images/logo.svg',
    '/images/skipper-icon-64.png',
  ];
  
  criticalAssets.forEach(asset => {
    const link = document.createElement('link');
    link.rel = 'preload';
    
    if (asset.endsWith('.css')) {
      link.as = 'style';
    } else if (asset.match(/\.(png|jpg|jpeg|gif|svg)$/)) {
      link.as = 'image';
    } else if (asset.endsWith('.js')) {
      link.as = 'script';
    }
    
    link.href = asset;
    document.head.appendChild(link);
  });
};

// Service worker for asset caching
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered successfully:', registration.scope);
      
      // Listen for updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New content is available
            if (window.confirm('New version available. Reload to update?')) {
              window.location.reload();
            }
          }
        });
      });
      
      return registration;
    } catch (error) {
      console.warn('Service Worker registration failed:', error);
    }
  }
};

// Resource prefetching strategy
export const prefetchResources = (urls) => {
  if (!Array.isArray(urls)) return;
  
  urls.forEach(url => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.appendChild(link);
  });
};

// Critical CSS inlining
export const inlineCriticalCSS = (css) => {
  const style = document.createElement('style');
  style.textContent = css;
  style.setAttribute('data-critical', 'true');
  document.head.appendChild(style);
};

// Font optimization
export const optimizeFonts = () => {
  // Preload critical fonts
  const criticalFonts = [
    '/fonts/roboto-v30-latin-300.woff2',
    '/fonts/roboto-v30-latin-regular.woff2',
    '/fonts/roboto-v30-latin-500.woff2',
  ];
  
  criticalFonts.forEach(font => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'font';
    link.type = 'font/woff2';
    link.crossOrigin = 'anonymous';
    link.href = font;
    document.head.appendChild(link);
  });
  
  // Set font-display: swap for better loading performance
  const style = document.createElement('style');
  style.textContent = `
    @font-face {
      font-family: 'Roboto';
      font-display: swap;
    }
  `;
  document.head.appendChild(style);
};

// Bundle size monitoring
export const monitorBundleSize = () => {
  if (process.env.NODE_ENV === 'development') {
    // Monitor JavaScript bundle size
    const scripts = document.querySelectorAll('script[src]');
    let totalSize = 0;
    
    scripts.forEach(script => {
      fetch(script.src, { method: 'HEAD' })
        .then(response => {
          const size = response.headers.get('content-length');
          if (size) {
            totalSize += parseInt(size);
            console.log(`Script ${script.src}: ${(size / 1024).toFixed(1)} KB`);
          }
        })
        .catch(() => {
          // Ignore CORS errors for external scripts
        });
    });
    
    setTimeout(() => {
      console.log(`Total JS bundle size: ${(totalSize / 1024).toFixed(1)} KB`);
      if (totalSize > 500 * 1024) {
        console.warn('⚠️ Bundle size exceeds 500KB. Consider code splitting.');
      }
    }, 1000);
  }
};

// Performance metrics collection
export const collectPerformanceMetrics = () => {
  if ('performance' in window) {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        if (entry.entryType === 'navigation') {
          console.log('Navigation timing:', {
            'DNS Lookup': entry.domainLookupEnd - entry.domainLookupStart,
            'TCP Connection': entry.connectEnd - entry.connectStart,
            'Request': entry.responseStart - entry.requestStart,
            'Response': entry.responseEnd - entry.responseStart,
            'DOM Processing': entry.domContentLoadedEventStart - entry.responseEnd,
            'Load Complete': entry.loadEventEnd - entry.loadEventStart,
            'Total': entry.loadEventEnd - entry.navigationStart
          });
        }
        
        if (entry.entryType === 'largest-contentful-paint') {
          console.log('Largest Contentful Paint:', entry.startTime);
          if (entry.startTime > 2500) {
            console.warn('⚠️ LCP is slow (>2.5s). Consider optimizing critical resources.');
          }
        }
        
        if (entry.entryType === 'first-input') {
          console.log('First Input Delay:', entry.processingStart - entry.startTime);
          if (entry.processingStart - entry.startTime > 100) {
            console.warn('⚠️ FID is slow (>100ms). Consider reducing JavaScript execution time.');
          }
        }
      });
    });
    
    observer.observe({ entryTypes: ['navigation', 'largest-contentful-paint', 'first-input'] });
  }
};

// Memory usage monitoring
export const monitorMemoryUsage = () => {
  if ('memory' in performance) {
    const memoryInfo = performance.memory;
    console.log('Memory usage:', {
      'Used': (memoryInfo.usedJSHeapSize / 1024 / 1024).toFixed(1) + ' MB',
      'Total': (memoryInfo.totalJSHeapSize / 1024 / 1024).toFixed(1) + ' MB',
      'Limit': (memoryInfo.jsHeapSizeLimit / 1024 / 1024).toFixed(1) + ' MB'
    });
    
    if (memoryInfo.usedJSHeapSize / memoryInfo.jsHeapSizeLimit > 0.7) {
      console.warn('⚠️ High memory usage detected. Consider optimizing component renders.');
    }
  }
};

// Initialize all optimizations
export const initializeOptimizations = () => {
  preloadCriticalAssets();
  optimizeFonts();
  registerServiceWorker();
  
  if (process.env.NODE_ENV === 'development') {
    monitorBundleSize();
    collectPerformanceMetrics();
    
    // Monitor memory usage every 30 seconds in development
    setInterval(monitorMemoryUsage, 30000);
  }
};